import type { DeepFile, DeepTodo } from "@/stores/ai-explorer-store"

import { categorizeFiles } from "./categorizeFiles"

/**
 * Escapes triple-backtick sequences in code fence content.
 * If file content contains "```", we bump the fence to "````".
 */
function escapeFence(content: string): string {
  if (content.includes("```")) {
    return "````"
  }
  return "```"
}

function formatElapsed(
  started_at: string | null | undefined,
  completed_at: string | null | undefined
): string {
  if (!started_at || !completed_at) return ""
  const start = new Date(started_at).getTime()
  const end = new Date(completed_at).getTime()
  const secs = Math.max(0, Math.round((end - start) / 1000))
  if (secs < 60) return ` (${secs}s)`
  const mins = Math.floor(secs / 60)
  const remainder = secs % 60
  return ` (${mins}m ${remainder}s)`
}

/**
 * D3: Export the current Deep Explorer scratchpad as a Markdown file.
 * Client-side only — uses Blob + URL.createObjectURL for the download.
 */
export function exportScratchpad({
  todos,
  files,
  sessionTitle,
}: {
  todos: DeepTodo[]
  files: DeepFile[]
  sessionTitle: string
}): void {
  const now = new Date()
  const iso = now.toISOString()
  const dateSlug = now.toISOString().slice(0, 10) // YYYY-MM-DD

  const todoSection =
    todos.length === 0
      ? "_No todos._\n"
      : todos
          .map((todo) => {
            const check = todo.status === "completed" ? "x" : " "
            const elapsed = formatElapsed(todo.started_at, todo.completed_at)
            return `- [${check}] ${todo.content || todo.text}${elapsed}`
          })
          .join("\n") + "\n"

  // Only export user-facing artifacts (plan / deliverable / verifications /
  // scratchpad / other root-level .md/.json). The `deepagents` filesystem
  // middleware also evicts large tool results to `/large_tool_results/<id>`,
  // but those are internal plumbing — the user doesn't want 50kB `call_abc…`
  // blobs in their markdown export.
  const { artifacts } = categorizeFiles(files)
  const filesToExport = artifacts.map((entry) => entry.file)

  const filesSection =
    filesToExport.length === 0
      ? "_No files._\n"
      : filesToExport
          .map((file) => {
            const fence = escapeFence(file.content)
            const ext = file.path.split(".").pop() ?? ""
            const lang = ext
            return `### ${file.path}\n\n${fence}${lang}\n${file.content}\n${fence}`
          })
          .join("\n\n") + "\n"

  const markdown = [
    `# Deep Explorer session — ${sessionTitle}`,
    `Exported ${iso}`,
    ``,
    `## Todos`,
    ``,
    todoSection,
    `## Files`,
    ``,
    filesSection,
  ].join("\n")

  // Sanitize filename
  const safeTitle = sessionTitle
    .replace(/[^a-z0-9\s-]/gi, "")
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase()
    .slice(0, 50)

  const filename = `deep-explorer-${safeTitle || "session"}-${dateSlug}.md`

  const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  // Cleanup after a tick
  setTimeout(() => {
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, 100)
}
